/**
 * Explore page translations — kept separate from the main i18n system
 * because of the large volume of game examples, mechanics, comparison rows, etc.
 *
 * Usage:  const et = useExploreTranslations();
 *         et("hero_imagine")
 */

const en = {
  // Hero
  hero_imagine: "You Can Imagine",

  // Feature cards below hero
  feat_combat_label: "Combat System",
  feat_combat_desc: "D&D-style turn-based battles",
  feat_org_label: "Org Management",
  feat_org_desc: "Build guilds, gangs & armies",
  feat_territory_label: "Territory Control",
  feat_territory_desc: "Claim & defend locations",
  feat_mp_label: "Multiplayer",
  feat_mp_desc: "Play with friends in real-time",
  feat_magic_label: "Magic System",
  feat_magic_desc: "Spells, mana & custom magic",
  feat_politics_label: "Politics & Influence",
  feat_politics_desc: "Factions, alliances & schemes",
  feat_journal_label: "Journal & Book Export",
  feat_journal_desc: "Export adventures as stories",
  feat_map_label: "Interactive World Map",
  feat_map_desc: "Visual map editor & navigation",
  feat_byoai_label: "Bring Your Own AI",
  feat_byoai_desc: "Custom AI for full creative freedom",
  feat_research_label: "Research Module",
  feat_research_desc: "Google, Wiki & Fandom built-in",

  // Categories
  cat_all: "All",
  cat_rts: "RTS & Strategy",
  cat_tycoon: "Tycoon & Management",
  cat_rpg: "Text RPG",
  cat_fanfic: "Inspired Worlds",
  cat_horror: "Horror & Survival",
  cat_other: "More Genres",

  // Tags (reusable across games)
  tag_territory_control: "Territory Control",
  tag_organization: "Organization",
  tag_heat_system: "Heat System",
  tag_spy_cell: "Spy Cell",
  tag_operations: "Operations",
  tag_intel_resources: "Intel Resources",
  tag_military_command: "Military Command",
  tag_territory: "Territory",
  tag_resources: "Resources",
  tag_criminal_syndicate: "Criminal Syndicate",
  tag_influence: "Influence",
  tag_feudal: "Feudal",
  tag_commanders: "Commanders",
  tag_cell_based: "Cell-based",
  tag_morale: "Morale",
  tag_economy: "Economy",
  tag_workforce: "Workforce",
  tag_reputation: "Reputation",
  tag_npc_management: "NPC Management",
  tag_survival: "Survival",
  tag_personnel: "Personnel",
  tag_quest_system: "Quest System",
  tag_magic_system: "Magic System",
  tag_quest_journal: "Quest Journal",
  tag_morality: "Morality",
  tag_region_movement: "Region Movement",
  tag_combat: "Combat",
  tag_lore_discovery: "Lore Discovery",
  tag_skill_checks: "Skill Checks",
  tag_character_progression: "Character Progression",
  tag_inner_voice: "Inner Voice",
  tag_inventory: "Inventory",
  tag_dialogue_system: "Dialogue System",

  // Comparison table feature names
  comp_freeform: "Free-form Interaction",
  comp_branching: "Branching & Player Choice",
  comp_memory: "Persistent Memory & Lore",
  comp_worldstate: "Dynamic World State",
  comp_custom_worlds: "Custom Worlds & Scenarios",
  comp_character: "Character System",
  comp_narrative: "Narrative Control",
  comp_rpg: "RPG Mechanics",
  comp_strategy: "Strategy Systems",
  comp_navigation: "World Navigation",
  comp_simulation: "Simulation Systems",
  comp_ai_flex: "AI Flexibility",
  comp_multi_agent: "Multi-Agent AI",
  comp_sharing: "Content & Sharing",
  comp_knowledge: "External Knowledge",
  comp_media: "Media & Audio",
  comp_multiplayer: "Multiplayer",
  comp_economy: "Economy & Resources",
  comp_morality: "Morality & Alignment",
  comp_consequence: "Consequence Engine",
  comp_survival: "Survival Mechanics",
  comp_lore: "Lore Discovery",
  comp_export: "Journal & Book Export",
  comp_custom_rules: "Custom Rules Engine",
  comp_document: "Document Upload & Analysis",
  // labels
  comp_label_advanced: "Advanced",
  comp_label_full_system: "Full system",
  comp_label_deep: "Deep",
  comp_label_advanced_rpg: "Advanced RPG",
  comp_label_ai_director: "AI Director",
  comp_label_strong: "Strong",
  comp_label_map_based: "Map-based",
  comp_label_full: "Full",
  comp_label_byo_ai: "Full (BYO AI)",
  comp_label_google_wiki: "Google, Wiki, Fandom",
  comp_label_tts: "Full (TTS + more)",

  // Mechanics
  mech_combat_title: "Combat System",
  mech_combat_desc: "D&D 5e-inspired turn-based combat with initiative, action economy (action, bonus action, reaction, movement), hit points, AC, and damage rolls.",
  mech_skill_title: "Skill Checks",
  mech_skill_desc: "Roll-based stat checks (STR, DEX, CON, INT, WIS, CHA) with proficiency bonuses, advantage/disadvantage, and difficulty class scaling.",
  mech_inventory_title: "Inventory & Items",
  mech_inventory_desc: "Carry weight, equipment slots, item properties, consumables, and loot tables. Equip gear that affects your stats and abilities.",
  mech_magic_title: "Magic System",
  mech_magic_desc: "Spell slots, mana pools, or custom magic. Define schools of magic, spell lists, casting requirements, and ritual mechanics.",
  mech_quest_title: "Quest Journal",
  mech_quest_desc: "Track main quests, side quests, and objectives. Quests can be discovered, accepted, progressed, completed, or failed dynamically.",
  mech_reputation_title: "Reputation & Factions",
  mech_reputation_desc: "Multi-faction reputation tracking. Your actions affect how NPCs and organizations treat you — from hostile to allied.",
  mech_morality_title: "Morality System",
  mech_morality_desc: "Track moral alignment across multiple axes. Choices have consequences that shift your character's ethical standing and NPC reactions.",
  mech_territory_title: "Territory Control",
  mech_territory_desc: "Claim, defend, and sabotage locations. Manage controlled territories with defense levels, resource production, and rival threats.",
  mech_org_title: "Organization Management",
  mech_org_desc: "Build and manage organizations — guilds, gangs, companies, armies. Recruit members, assign roles, manage morale, and handle internal politics.",
  mech_economy_title: "Economy & Trade",
  mech_economy_desc: "Currency systems, shops, trading, crafting costs, and dynamic pricing. Run businesses, manage cash flow, and corner markets.",
  mech_resources_title: "Resources",
  mech_resources_desc: "Track custom resources (food, ammo, influence, heat). Resources deplete, regenerate, and can be produced at controlled locations.",
  mech_survival_title: "Survival",
  mech_survival_desc: "Hunger, thirst, fatigue, and exposure mechanics. Forage, hunt, purify water, and find shelter to stay alive in harsh environments.",
  mech_dialogue_title: "Dialogue System",
  mech_dialogue_desc: "Rich NPC conversations with persuasion, intimidation, deception, and insight checks. NPCs remember what you've said and done.",
  mech_worldmap_title: "World Map",
  mech_worldmap_desc: "Interactive world maps with placeable nodes, sub-maps, and location linking. Navigate visually and drill into regions.",
  mech_region_title: "Region Movement",
  mech_region_desc: "Travel between locations with time costs, random encounters, fast-travel unlocks, and terrain-based movement modifiers.",
  mech_time_title: "Time & Weather",
  mech_time_desc: "Day/night cycles, seasonal changes, and weather systems that affect gameplay, visibility, NPC schedules, and survival.",
  mech_progression_title: "Character Progression",
  mech_progression_desc: "Level up, gain XP, unlock abilities, and advance through class-based or freeform progression systems.",
  mech_classes_title: "Character Classes",
  mech_classes_desc: "Define custom classes with unique abilities, stat bonuses, equipment proficiencies, and progression paths.",
  mech_conditions_title: "Conditions & Status",
  mech_conditions_desc: "14 D&D-style conditions (poisoned, stunned, invisible, etc.) plus 6-tier exhaustion. Conditions affect rolls, movement, and abilities.",
  mech_influence_title: "Influence & Politics",
  mech_influence_desc: "Political power systems — accumulate influence over factions, broker deals, form alliances, and scheme for control.",
  mech_consequence_title: "Consequence Engine",
  mech_consequence_desc: "Actions have rippling consequences. The AI tracks cause and effect chains that surface hours or sessions later.",
  mech_tone_title: "Narrative Tone",
  mech_tone_desc: "Control the story's feel — gritty realism, heroic fantasy, dark comedy, horror, or literary prose. Adjustable per-world.",
  mech_pacing_title: "Encounter Pacing",
  mech_pacing_desc: "AI director manages tension curves — alternating combat, exploration, social, and rest scenes for dynamic pacing.",
  mech_lore_title: "Lore Discovery",
  mech_lore_desc: "Hidden world lore revealed through exploration, NPC conversations, and item examination. Track what the player knows vs. what exists.",
  mech_inner_title: "Inner Voice",
  mech_inner_desc: "Character's internal monologue reacts to events with personality-driven thoughts, gut feelings, and moral reflections.",
  mech_card_title: "Card Game",
  mech_card_desc: "Collectible card mechanics — draw hands, play cards for actions, build decks. Cards can represent spells, items, or abilities.",
  mech_timeline_title: "Timeline",
  mech_timeline_desc: "Visual timeline tracking major events, discoveries, deaths, and milestones throughout your adventure's history.",
  mech_fanfic_title: "Online Research",
  mech_fanfic_desc: "Import reference material from the web. Built-in Google Search, Wikipedia, and Fandom wiki let the AI absorb lore, facts, and world rules to enrich your story.",
  mech_multiplayer_title: "Multiplayer",
  mech_multiplayer_desc: "Play with friends in shared adventures. Round-robin turns, initiative order, or free-form — with party management and shared quests.",
  mech_custom_title: "Custom Rules",
  mech_custom_desc: "Define your own game rules in plain text. The AI enforces them alongside built-in mechanics for fully custom gameplay.",
  mech_research_title: "Research Module",
  mech_research_desc: "Built-in Google Search, Wikipedia, and Fandom wiki integration. Auto-import lore, reference images, and canonical facts into your world's knowledge base.",

  // Game examples
  game_cartel_title: "Cartel Wars",
  game_cartel_desc: "Build a drug empire from a street corner to a continental operation. Manage territory, recruit soldiers, bribe officials, and wage war against rival cartels.",
  game_shadow_title: "Shadow Cell",
  game_shadow_desc: "Run a covert intelligence agency during the Cold War. Recruit agents, plan operations, manage safe houses, and prevent double agents from compromising your network.",
  game_galactic_title: "Galactic Conquest",
  game_galactic_desc: "Command a star fleet across a procedural galaxy. Colonize planets, build shipyards, research technology, and engage rival empires in strategic warfare.",
  game_godfather_title: "The Godfather's Heir",
  game_godfather_desc: "Inherit a crumbling mafia family. Rebuild through protection rackets, political corruption, and eliminating rivals — all while keeping the FBI off your trail.",
  game_feudal_title: "Feudal Overlord",
  game_feudal_desc: "Unite warring medieval provinces under your banner. Appoint lords, collect tributes, arrange marriages, and lead crusading armies against invaders.",
  game_rebel_title: "Rebel Command",
  game_rebel_desc: "Lead an underground resistance against a dystopian regime. Manage cells, plan sabotage missions, recruit civilians, and broadcast pirate radio to inspire revolution.",
  game_dungeon_title: "Dungeon Inc.",
  game_dungeon_desc: "Run a dungeon as a business. Hire monsters, design traps, set loot tables, and balance adventurer satisfaction against profitability. Five-star reviews matter.",
  game_potion_title: "Potion Empire",
  game_potion_desc: "Start a humble apothecary and grow it into a pharmaceutical empire. Source rare ingredients, research recipes, manage supply chains, and outmaneuver alchemist guilds.",
  game_tavern_title: "Tavern Keeper",
  game_tavern_desc: "Build the greatest tavern in the realm. Hire barkeeps, source exotic ales, host bard competitions, handle bar brawls, and attract legendary adventurers.",
  game_station_title: "Space Station Alpha",
  game_station_desc: "Manage a frontier space station. Balance oxygen supply, trade with alien merchants, handle docking disputes, and defend against pirates — all while turning a profit.",
  game_crime_title: "Crime Lord Simulator",
  game_crime_desc: "Build a criminal empire from scratch. Launder money, run fronts, corrupt politicians, manage enforcers, and navigate the razor's edge between power and prison.",
  game_gladiator_title: "Gladiator School",
  game_gladiator_desc: "Train gladiators and manage a ludus in ancient Rome. Buy slaves, train fighters, negotiate with senators, rig matches, and climb the social ladder through blood sport.",
  game_mercenary_title: "Mercenary Company",
  game_mercenary_desc: "Run a free company of sellswords. Take contracts, manage payroll, equip your troops, negotiate with kings, and build your company's legend across the continent.",
  game_hogwarts_title: "Enchanted Academy",
  game_hogwarts_desc: "Enroll in a school of sorcery. Choose your house, attend classes, master spells, uncover secrets, and face dark forces — with the AI as your wise headmaster.",
  game_westeros_title: "Iron Kingdoms",
  game_westeros_desc: "Play as a minor noble house in a war-torn continent. Navigate deadly politics through alliances, betrayals, marriages, and war. The cold winds are always coming.",
  game_middleearth_title: "Ancient Wilds Ranger",
  game_middleearth_desc: "Patrol untamed wilderness as a lone ranger. Track dark creatures, protect settlements, gather intelligence, and uncover a growing shadow threatening the free lands.",
  game_starfleet_title: "Stellar Academy",
  game_starfleet_desc: "Enroll in an elite space fleet academy. Study xenolinguistics, pass impossible simulations, befriend alien cadets, and earn your commission aboard a starship.",
  game_jedi_title: "Exile of the Order",
  game_jedi_desc: "A fallen knight of an ancient order, hunted by a rising empire. Hide among civilians, resist corruption, train in secret, and decide whether to fight back or disappear forever.",
  game_peaky_title: "Razor Gang: Rise",
  game_peaky_desc: "Build your own post-war criminal empire in an industrial city. Run racetracks, fight rival gangs, corrupt officials, and expand from the slums to parliament.",
  game_naruto_title: "Shadow Village",
  game_naruto_desc: "Lead your own hidden ninja village. Train warriors, assign missions, manage alliances with other villages, and prepare for the next great shinobi war.",
  game_darkest_title: "Darkest Descent",
  game_darkest_desc: "Explore a cursed megadungeon floor by floor. Manage torchlight, sanity, and dwindling supplies. Every door is a gamble between treasure and terrible death.",
  game_wasteland_title: "Wasteland Wanderer",
  game_wasteland_desc: "Survive a post-apocalyptic wasteland. Scavenge for food, water, and ammunition. Trade with settlements, avoid raiders, and uncover what caused the fall.",
  game_court_title: "Court of Thorns",
  game_court_desc: "Navigate deadly fae politics as a mortal trapped in the Seelie Court. Every word is a binding contract, every gift has strings, and beauty hides cruelty.",
  game_pirate_title: "Pirate Captain",
  game_pirate_desc: "Captain a pirate ship across the Caribbean. Manage your crew, plunder merchant vessels, find buried treasure, and become the most feared name on the seas.",
  game_vampire_title: "Vampire: Eternal Night",
  game_vampire_desc: "Navigate undead society as a newly turned vampire. Feed without getting caught, build your domain, navigate elder politics, and choose between humanity and power.",
  game_courtroom_title: "Courtroom Drama",
  game_courtroom_desc: "Play as a defense attorney in a gritty city. Interview witnesses, find evidence, build your case, and argue before the jury. Every client has secrets.",
  game_island_title: "Survival Island",
  game_island_desc: "Stranded after a plane crash. Build shelter, find food, explore the island's mysteries, and decide whether to cooperate or compete with other survivors.",
  game_heist_title: "Heist Crew",
  game_heist_desc: "Assemble a crew of specialists and plan the perfect heist. Case the target, recruit talent, gather equipment, and execute — or improvise when everything goes wrong.",
  game_political_title: "Political Thriller",
  game_political_desc: "Run for president in a fictional nation. Manage campaigns, debate rivals, handle scandals, make promises you may not keep, and navigate a web of donors and lobbyists.",
  game_sky_title: "Sky Citadel",
  game_sky_desc: "Rule a floating fortress above the storm clouds. Manage airship fleets, negotiate with sky-pirate clans, mine storm crystals, and defend against rival citadels vying for altitude supremacy.",
  game_underdark_title: "Underdark Colony",
  game_underdark_desc: "Establish a settlement deep beneath the earth among bioluminescent fungi and hostile cave-dwellers. Manage light as a resource, farm mushrooms, and broker peace between subterranean factions.",
  game_clockwork_title: "Clockwork City",
  game_clockwork_desc: "Investigate conspiracies in a Victorian metropolis powered by colossal gears. Join secret societies, solve clockwork puzzles, and uncover who — or what — controls the Grand Mechanism.",
  game_arcane_title: "Arcane Academy",
  game_arcane_desc: "Enroll in a college of forbidden magic. Attend lectures, duel classmates, research ancient tomes, and decide whether to obey the faculty or unlock the sealed vault beneath the library.",
  game_sand_title: "Sand Kingdoms",
  game_sand_desc: "Wander a vast desert dotted with buried civilizations. Excavate relics, trade with nomadic tribes, survive sandstorms, and piece together a prophecy that could raise or ruin an empire.",
  game_frozen_title: "Frozen Throne",
  game_frozen_desc: "Lead a clan through an endless winter. Hunt dire beasts, raid enemy camps, appease the old gods, and unite the northern tribes before the ice swallows everything.",
  game_verdant_title: "Verdant Spire",
  game_verdant_desc: "Govern a treetop city woven into a world-tree. Balance nature magic against industrial progress, negotiate with forest spirits, and defend against loggers encroaching from the lowlands.",
  game_deep_title: "Deep Haven",
  game_deep_desc: "Manage an underwater domed city on the ocean floor. Balance oxygen supply, trade with surface ships, study alien deep-sea creatures, and prevent catastrophic hull breaches.",
  game_neon_title: "Neon Pit",
  game_neon_desc: "Rise through the ranks of an underground cyberpunk fight circuit. Augment your body, manage sponsors, rig matches, and decide if the championship is worth your humanity.",
  game_plague_title: "Plague Ward",
  game_plague_desc: "You're a healer in a medieval city ravaged by plague. Triage patients, source rare herbs, navigate quarantine politics, and decide who lives when medicine runs low.",
  game_iron_title: "Iron Rail",
  game_iron_desc: "Command an armored train crossing a shattered continent. Pick up survivors, defend against raiders, trade fuel for food, and choose which settlements to save — you can't stop everywhere.",
  game_silk_title: "Silk Road",
  game_silk_desc: "Lead a merchant caravan across a magical desert. Negotiate with djinn, evade bandits, discover oasis cities, and accumulate wealth trading enchanted goods between distant kingdoms.",
  game_hw_title: "The Wasteland",
  game_hw_desc: "Civilization fell. Now there's only the endless cracked earth, toxic storms, and the things that hunt at night. Scavenge irradiated ruins, trade with desperate survivors, and decide what you're willing to become to see another sunrise.",
  game_hh_title: "Whispering Manor",
  game_hh_desc: "You inherited a Victorian mansion. The doors lock themselves. The mirrors show the wrong reflection. Something lives in the walls and it knows your name. Explore room by room, uncover the family's dark history, and survive until dawn.",
  game_hu_title: "Descent to the Underworld",
  game_hu_desc: "Cross the River Styx. Bargain with Charon. Navigate the circles of the dead, outwit ancient guardians, and retrieve a soul from the domain of death itself. Every step deeper makes the way back harder.",
  game_hi_title: "The Island",
  game_hi_desc: "Your plane crashed on a mysterious island. Strange electromagnetic anomalies. Numbers that repeat everywhere. Other survivors with impossible pasts. Something moves in the jungle — and the island doesn't want you to leave.",
  game_hg_title: "The Triangle",
  game_hg_desc: "A derelict ship appears in the Bermuda Triangle. Your crew boards it to find it's a time loop — the same ship, the same deaths, repeating forever. Break the cycle or become part of it. Each loop reveals new horrors.",
  game_ha_title: "Xenomorph: Deep Space",
  game_ha_desc: "Your mining vessel detected an unknown signal. Now something is aboard — fast, intelligent, and breeding. Seal bulkheads, ration oxygen, track it on motion sensors, and decide who gets the last escape pod.",
  game_hv_title: "Invasion Day",
  game_hv_desc: "They came without warning. Massive ships over every major city. Communications are dead. The military is gone. Lead a small group of survivors through a burning metropolis, scavenge weapons, and find the resistance — if it exists.",
  game_has_title: "Ward 13",
  game_has_desc: "You wake up in an abandoned asylum with no memory. Patient files describe experiments. The PA system crackles with a voice that knows you. Something behind the locked ward door is scratching. Piece together what happened — before it happens again.",
};

/** Union of all valid explore translation keys, derived from the English dict. */
export type ExploreKey = keyof typeof en;

/**
 * Partial record of explore keys — other languages only need to provide the
 * keys they have translated; missing keys fall back to English at runtime.
 */
type ExploreDict = Partial<Record<ExploreKey, string>>;

const he: ExploreDict = {
  hero_imagine: "שתוכלו לדמיין",

  feat_combat_label: "מערכת קרב",
  feat_combat_desc: "קרבות מבוססי תורות בסגנון D&D",
  feat_org_label: "ניהול ארגון",
  feat_org_desc: "בנו גילדות, כנופיות וצבאות",
  feat_territory_label: "שליטה טריטוריאלית",
  feat_territory_desc: "כבשו והגנו על מיקומים",
  feat_mp_label: "מולטיפלייר",
  feat_mp_desc: "שחקו עם חברים בזמן אמת",
  feat_magic_label: "מערכת קסם",
  feat_magic_desc: "כישופים, מאנה וקסם מותאם",
  feat_politics_label: "פוליטיקה והשפעה",
  feat_politics_desc: "סיעות, בריתות ומזימות",
  feat_journal_label: "יומן וייצוא ספר",
  feat_journal_desc: "ייצאו הרפתקאות כסיפורים",
  feat_map_label: "מפת עולם אינטראקטיבית",
  feat_map_desc: "עורך מפות ויזואלי וניווט",
  feat_byoai_label: "הביאו AI משלכם",
  feat_byoai_desc: "AI מותאם לחופש יצירתי מלא",
  feat_research_label: "מודול מחקר",
  feat_research_desc: "Google, ויקי ו-Fandom מובנים",

  cat_all: "הכל",
  cat_rts: "אסטרטגיה",
  cat_tycoon: "ניהול וטייקון",
  cat_rpg: "RPG טקסטואלי",
  cat_fanfic: "עולמות בהשראה",
  cat_horror: "אימה והישרדות",
  cat_other: "ז׳אנרים נוספים",

  tag_territory_control: "שליטה טריטוריאלית",
  tag_organization: "ארגון",
  tag_heat_system: "מערכת חום",
  tag_spy_cell: "תא ריגול",
  tag_operations: "מבצעים",
  tag_intel_resources: "משאבי מודיעין",
  tag_military_command: "פיקוד צבאי",
  tag_territory: "טריטוריה",
  tag_resources: "משאבים",
  tag_criminal_syndicate: "סינדיקט פלילי",
  tag_influence: "השפעה",
  tag_feudal: "פיאודלי",
  tag_commanders: "מפקדים",
  tag_cell_based: "מבוסס תאים",
  tag_morale: "מורל",
  tag_economy: "כלכלה",
  tag_workforce: "כוח עבודה",
  tag_reputation: "מוניטין",
  tag_npc_management: "ניהול דמויות",
  tag_survival: "הישרדות",
  tag_personnel: "כוח אדם",
  tag_quest_system: "מערכת משימות",
  tag_magic_system: "מערכת קסם",
  tag_quest_journal: "יומן משימות",
  tag_morality: "מוסר",
  tag_region_movement: "תנועה אזורית",
  tag_combat: "קרב",
  tag_lore_discovery: "גילוי לור",
  tag_skill_checks: "בדיקות מיומנות",
  tag_character_progression: "התקדמות דמות",
  tag_inner_voice: "קול פנימי",
  tag_inventory: "מלאי",
  tag_dialogue_system: "מערכת דיאלוג",

  comp_freeform: "אינטראקציה חופשית",
  comp_branching: "הסתעפות ובחירת שחקן",
  comp_memory: "זיכרון ולור מתמשכים",
  comp_worldstate: "מצב עולם דינמי",
  comp_custom_worlds: "עולמות ותרחישים מותאמים",
  comp_character: "מערכת דמויות",
  comp_narrative: "שליטה נרטיבית",
  comp_rpg: "מכניקות RPG",
  comp_strategy: "מערכות אסטרטגיה",
  comp_navigation: "ניווט בעולם",
  comp_simulation: "מערכות סימולציה",
  comp_ai_flex: "גמישות AI",
  comp_multi_agent: "AI מרובה סוכנים",
  comp_sharing: "תוכן ושיתוף",
  comp_knowledge: "ידע חיצוני",
  comp_media: "מדיה ואודיו",
  comp_multiplayer: "מולטיפלייר",
  comp_economy: "כלכלה ומשאבים",
  comp_morality: "מוסר ואוריינטציה",
  comp_consequence: "מנוע תוצאות",
  comp_survival: "מכניקות הישרדות",
  comp_lore: "גילוי לור",
  comp_export: "יומן וייצוא ספר",
  comp_custom_rules: "מנוע חוקים מותאם",
  comp_document: "העלאת מסמכים וניתוח",
  comp_label_advanced: "מתקדם",
  comp_label_full_system: "מערכת מלאה",
  comp_label_deep: "עמוק",
  comp_label_advanced_rpg: "RPG מתקדם",
  comp_label_ai_director: "במאי AI",
  comp_label_strong: "חזק",
  comp_label_map_based: "מבוסס מפה",
  comp_label_full: "מלא",
  comp_label_byo_ai: "מלא (AI משלכם)",
  comp_label_google_wiki: "Google, ויקי, Fandom",
  comp_label_tts: "מלא (TTS + עוד)",

  mech_combat_title: "מערכת קרב",
  mech_combat_desc: "קרב מבוסס תורות בהשראת D&D 5e עם יוזמה, כלכלת פעולות (פעולה, פעולת בונוס, תגובה, תנועה), נקודות חיים, AC וגלגולי נזק.",
  mech_skill_title: "בדיקות מיומנות",
  mech_skill_desc: "בדיקות מבוססות גלגול (STR, DEX, CON, INT, WIS, CHA) עם בונוסי מיומנות, יתרון/חיסרון ודרגות קושי.",
  mech_inventory_title: "מלאי ופריטים",
  mech_inventory_desc: "משקל נשיאה, חריצי ציוד, תכונות פריטים, חומרים מתכלים וטבלאות שלל. ציוד שמשפיע על הסטטיסטיקות שלכם.",
  mech_magic_title: "מערכת קסם",
  mech_magic_desc: "חריצי כישוף, מאגרי מאנה, או קסם מותאם. הגדירו אסכולות קסם, רשימות כישופים, דרישות הטלה ומכניקות טקסים.",
  mech_quest_title: "יומן משימות",
  mech_quest_desc: "עקבו אחרי משימות ראשיות, צדדיות ומטרות. משימות מתגלות, מתקבלות, מתקדמות, מושלמות או נכשלות באופן דינמי.",
  mech_reputation_title: "מוניטין וסיעות",
  mech_reputation_desc: "מעקב מוניטין רב-סיעתי. המעשים שלכם משפיעים על איך דמויות וארגונים מתייחסים אליכם — מעוינים ועד בני ברית.",
  mech_morality_title: "מערכת מוסר",
  mech_morality_desc: "מעקב אחרי כיוון מוסרי על מספר צירים. לבחירות יש תוצאות שמשנות את העמדה האתית של הדמות ואת תגובות הדמויות.",
  mech_territory_title: "שליטה טריטוריאלית",
  mech_territory_desc: "כבשו, הגנו וחבלו במיקומים. נהלו טריטוריות עם רמות הגנה, ייצור משאבים ואיומי יריבים.",
  mech_org_title: "ניהול ארגון",
  mech_org_desc: "בנו ונהלו ארגונים — גילדות, כנופיות, חברות, צבאות. גייסו חברים, שייכו תפקידים, נהלו מורל והתמודדו עם פוליטיקה פנימית.",
  mech_economy_title: "כלכלה ומסחר",
  mech_economy_desc: "מערכות מטבע, חנויות, מסחר, עלויות ייצור ותמחור דינמי. הפעילו עסקים, נהלו תזרים מזומנים ושלטו בשווקים.",
  mech_resources_title: "משאבים",
  mech_resources_desc: "עקבו אחרי משאבים מותאמים (מזון, תחמושת, השפעה, חום). משאבים נגמרים, מתחדשים ואפשר לייצר אותם במיקומים נשלטים.",
  mech_survival_title: "הישרדות",
  mech_survival_desc: "מכניקות רעב, צמא, עייפות וחשיפה. לקטו, צודו, טהרו מים ומצאו מחסה כדי לשרוד בסביבות עוינות.",
  mech_dialogue_title: "מערכת דיאלוג",
  mech_dialogue_desc: "שיחות עשירות עם דמויות הכוללות שכנוע, הפחדה, הונאה ובדיקות תובנה. הדמויות זוכרות מה אמרתם ועשיתם.",
  mech_worldmap_title: "מפת עולם",
  mech_worldmap_desc: "מפות עולם אינטראקטיביות עם נקודות הניתנות למיקום, תת-מפות וקישור מיקומים. נווטו ויזואלית וצללו לאזורים.",
  mech_region_title: "תנועה אזורית",
  mech_region_desc: "נסיעה בין מיקומים עם עלויות זמן, מפגשים אקראיים, פתיחת נסיעה מהירה ומשנים תנועה מבוססי שטח.",
  mech_time_title: "זמן ומזג אוויר",
  mech_time_desc: "מחזורי יום/לילה, שינויים עונתיים ומערכות מזג אוויר שמשפיעות על משחק, ראות, לוחות זמנים של דמויות והישרדות.",
  mech_progression_title: "התקדמות דמות",
  mech_progression_desc: "עלו רמה, צברו XP, פתחו יכולות והתקדמו דרך מערכות מבוססות מחלקה או חופשיות.",
  mech_classes_title: "מחלקות דמויות",
  mech_classes_desc: "הגדירו מחלקות מותאמות עם יכולות ייחודיות, בונוסי סטטיסטיקות, מיומנויות ציוד ומסלולי התקדמות.",
  mech_conditions_title: "מצבים וסטטוס",
  mech_conditions_desc: "14 מצבים בסגנון D&D (מורעל, מוקפא, בלתי נראה ועוד) בתוספת 6 רמות תשישות. מצבים משפיעים על גלגולים, תנועה ויכולות.",
  mech_influence_title: "השפעה ופוליטיקה",
  mech_influence_desc: "מערכות כוח פוליטי — צברו השפעה על סיעות, תווכו עסקאות, צרו בריתות ותכננו מזימות לשליטה.",
  mech_consequence_title: "מנוע תוצאות",
  mech_consequence_desc: "לפעולות יש תוצאות מתגלגלות. ה-AI עוקב אחרי שרשראות סיבה ותוצאה שצצות שעות או מפגשים מאוחר יותר.",
  mech_tone_title: "טון נרטיבי",
  mech_tone_desc: "שלטו בתחושת הסיפור — ריאליזם קשוח, פנטזיה הרואית, קומדיה אפלה, אימה או פרוזה ספרותית. ניתן להתאמה לכל עולם.",
  mech_pacing_title: "קצב מפגשים",
  mech_pacing_desc: "במאי ה-AI מנהל עקומות מתח — מתחלף בין קרב, חקירה, מפגשים חברתיים ומנוחה לקצב דינמי.",
  mech_lore_title: "גילוי לור",
  mech_lore_desc: "לור עולמי נסתר שנחשף דרך חקירה, שיחות עם דמויות ובחינת פריטים. עקבו אחרי מה שהשחקן יודע לעומת מה שקיים.",
  mech_inner_title: "קול פנימי",
  mech_inner_desc: "המונולוג הפנימי של הדמות מגיב לאירועים עם מחשבות מונעות אישיות, תחושות בטן ומשקפים מוסריים.",
  mech_card_title: "משחק קלפים",
  mech_card_desc: "מכניקות קלפים אספניים — משכו ידיים, שחקו קלפים לפעולות, בנו חפיסות. קלפים יכולים לייצג כישופים, פריטים או יכולות.",
  mech_timeline_title: "ציר זמן",
  mech_timeline_desc: "ציר זמן ויזואלי שעוקב אחרי אירועים מרכזיים, גילויים, מוות ואבני דרך לאורך ההרפתקה.",
  mech_fanfic_title: "מחקר מקוון",
  mech_fanfic_desc: "ייבוא חומר ייחוס מהרשת. חיפוש Google, ויקיפדיה ו-Fandom מובנים מאפשרים ל-AI לספוג לור, עובדות וחוקי עולם להעשרת הסיפור.",
  mech_multiplayer_title: "מולטיפלייר",
  mech_multiplayer_desc: "שחקו עם חברים בהרפתקאות משותפות. תורות מעגליות, סדר יוזמה או חופשי — עם ניהול קבוצה ומשימות משותפות.",
  mech_custom_title: "חוקים מותאמים",
  mech_custom_desc: "הגדירו חוקי משחק משלכם בטקסט חופשי. ה-AI אוכף אותם לצד מכניקות מובנות למשחק מותאם לחלוטין.",
  mech_research_title: "מודול מחקר",
  mech_research_desc: "שילוב מובנה של חיפוש Google, ויקיפדיה ו-Fandom. ייבוא אוטומטי של לור, תמונות ייחוס ועובדות קנוניות לבסיס הידע של העולם.",

  game_cartel_title: "מלחמות קרטלים",
  game_cartel_desc: "בנו אימפריית סמים מפינת רחוב למבצע יבשתי. נהלו טריטוריה, גייסו חיילים, שחדו פקידים ולחמו נגד קרטלים יריבים.",
  game_shadow_title: "תא צל",
  game_shadow_desc: "הפעילו סוכנות מודיעין חשאית בתקופת המלחמה הקרה. גייסו סוכנים, תכננו מבצעים, נהלו מסתורים ומנעו מסוכנים כפולים לחשוף את הרשת.",
  game_galactic_title: "כיבוש גלקטי",
  game_galactic_desc: "פקדו על צי כוכבים לאורך גלקסיה פרוצדורלית. התיישבו בפלנטות, בנו מספנות, חקרו טכנולוגיה והילחמו באימפריות יריבות.",
  game_godfather_title: "יורש הסנדק",
  game_godfather_desc: "ירשתם משפחת מאפיה מתמוטטת. בנו מחדש דרך סחיטה, שחיתות פוליטית וחיסול יריבים — תוך הרחקת ה-FBI.",
  game_feudal_title: "אדון פיאודלי",
  game_feudal_desc: "איחדו מחוזות ימי ביניים לוחמים תחת דגלכם. מנו אצילים, גבו מסים, סדרו נישואים והובילו צבאות נגד פולשים.",
  game_rebel_title: "פיקוד מרד",
  game_rebel_desc: "הובילו מחתרת נגד משטר דיסטופי. נהלו תאים, תכננו חבלות, גייסו אזרחים ושדרו רדיו פיראטי להשראת מהפכה.",
  game_dungeon_title: "דנג׳ן בע״מ",
  game_dungeon_desc: "הפעילו מבוך כעסק. שכרו מפלצות, עצבו מלכודות, הגדירו טבלאות שלל ואזנו שביעות רצון הרפתקנים מול רווחיות. ביקורות חמישה כוכבים חשובות.",
  game_potion_title: "אימפריית שיקויים",
  game_potion_desc: "התחילו כבית מרקחת צנוע וצמחו לאימפריה פרמצבטית. מצאו מרכיבים נדירים, חקרו מתכונים, נהלו שרשראות אספקה והערימו על גילדות אלכימיה.",
  game_tavern_title: "בעל הפונדק",
  game_tavern_desc: "בנו את הפונדק הגדול בממלכה. שכרו ברמנים, מצאו בירות אקזוטיות, ארחו תחרויות בארדים, טפלו בקטטות ומשכו הרפתקנים אגדיים.",
  game_station_title: "תחנת חלל אלפא",
  game_station_desc: "נהלו תחנת חלל גבולית. אזנו אספקת חמצן, סחרו עם סוחרים חייזרים, טפלו בסכסוכי עגינה והגנו מפני שודדים — תוך יצירת רווח.",
  game_crime_title: "סימולטור בוס פשע",
  game_crime_desc: "בנו אימפריה פלילית מאפס. הלבינו כסף, הפעילו חזיתות, שחתו פוליטיקאים, נהלו אנשי אכיפה ונווטו על הקצה בין כוח לכלא.",
  game_gladiator_title: "בית ספר לגלדיאטורים",
  game_gladiator_desc: "אמנו גלדיאטורים ונהלו לודוס ברומא העתיקה. קנו עבדים, אמנו לוחמים, נהלו מו״מ עם סנטורים, תרגלו קרבות וטפסו בסולם החברתי.",
  game_mercenary_title: "חברת שכירי חרב",
  game_mercenary_desc: "הפעילו חברה חופשית של שכירי חרב. קבלו חוזים, נהלו שכר, ציידו חיילים, נהלו מו״מ עם מלכים ובנו אגדה.",
  game_hogwarts_title: "האקדמיה הקסומה",
  game_hogwarts_desc: "הירשמו לבית ספר לכישוף. בחרו בית, למדו כישופים, חשפו סודות והתמודדו עם כוחות אפלים — עם ה-AI כמנהל החכם שלכם.",
  game_westeros_title: "ממלכות הברזל",
  game_westeros_desc: "שחקו כבית אצילים מינורי ביבשת שסועת מלחמה. נווטו בפוליטיקה קטלנית דרך בריתות, בגידות, נישואים ומלחמה. הרוחות הקרות תמיד מתקרבות.",
  game_middleearth_title: "סייר הפראות העתיקה",
  game_middleearth_desc: "סיירו בטבע הפראי כסייר בודד. עקבו אחרי יצורי חושך, הגנו על יישובים, אספו מודיעין וחשפו את הצל הגובר שמאיים על הארצות החופשיות.",
  game_starfleet_title: "האקדמיה הכוכבית",
  game_starfleet_desc: "הירשמו לאקדמיית צי החלל העילית. למדו בלשנות חייזרית, עברו סימולציות בלתי אפשריות, התיידדו עם חניכים חייזרים וקבלו מינוי על ספינת כוכבים.",
  game_jedi_title: "מגורש מהמסדר",
  game_jedi_desc: "אביר שנפל ממסדר עתיק, נרדף על ידי אימפריה עולה. הסתתרו בין אזרחים, עמדו בפני השחיתות, התאמנו בסתר והחליטו אם להילחם חזרה או להיעלם.",
  game_peaky_title: "כנופיית התער: עלייה",
  game_peaky_desc: "בנו אימפריה פלילית בעיר תעשייתית אחרי המלחמה. הפעילו מסלולי מירוצים, הילחמו בכנופיות, שחתו פקידים והתרחבו מהשכונה לפרלמנט.",
  game_naruto_title: "כפר הצללים",
  game_naruto_desc: "הובילו כפר נינג׳ה נסתר משלכם. אמנו לוחמים, שייכו משימות, נהלו בריתות עם כפרים אחרים והתכוננו למלחמת השינובי הבאה.",
  game_darkest_title: "הירידה האפלה",
  game_darkest_desc: "חקרו מגה-מבוך מקולל קומה אחרי קומה. נהלו אור לפידים, שפיות ואספקה מתדלדלת. כל דלת היא הימור בין אוצר למוות נורא.",
  game_wasteland_title: "נודד השממה",
  game_wasteland_desc: "שרדו בשממה פוסט-אפוקליפטית. חפשו מזון, מים ותחמושת. סחרו עם יישובים, הימנעו משודדים וגלו מה גרם לנפילה.",
  game_court_title: "חצר הקוצים",
  game_court_desc: "נווטו בפוליטיקה קטלנית של פיות כבני תמותה הלכודים בחצר הסילי. כל מילה היא חוזה מחייב, כל מתנה מגיעה עם מחירים, ויופי מסתיר אכזריות.",
  game_pirate_title: "קברניט פיראטים",
  game_pirate_desc: "קברנטו ספינת פיראטים ברחבי הקריביים. נהלו צוות, שללו ספינות סוחר, מצאו אוצרות קבורים והפכו לשם המפחיד ביותר בימים.",
  game_vampire_title: "ערפד: לילה נצחי",
  game_vampire_desc: "נווטו בחברה של מתים-חיים כערפד שזה עתה הפך. האכילו מבלי להיתפס, בנו שטח שליטה, נווטו בפוליטיקה של זקנים ובחרו בין אנושיות לכוח.",
  game_courtroom_title: "דרמת בית משפט",
  game_courtroom_desc: "שחקו כעורך דין פלילי בעיר קשוחה. חקרו עדים, מצאו ראיות, בנו תיק וטענו בפני חבר מושבעים. לכל לקוח יש סודות.",
  game_island_title: "אי הישרדות",
  game_island_desc: "נתקעתם אחרי התרסקות מטוס. בנו מחסה, מצאו מזון, חקרו את תעלומות האי והחליטו אם לשתף פעולה או להתחרות עם ניצולים אחרים.",
  game_heist_title: "צוות שוד",
  game_heist_desc: "הרכיבו צוות מומחים ותכננו את השוד המושלם. סקרו את המטרה, גייסו כישרונות, אספו ציוד ובצעו — או אלתרו כשהכל משתבש.",
  game_political_title: "מותחן פוליטי",
  game_political_desc: "התמודדו לנשיאות במדינה בדיונית. נהלו קמפיינים, התעמתו עם יריבים, טפלו בשערוריות, הבטיחו הבטחות ונווטו ברשת של תורמים ולוביסטים.",
  game_sky_title: "מצודת השמיים",
  game_sky_desc: "שלטו במבצר מרחף מעל העננים. נהלו צי ספינות אוויר, נהלו מו״מ עם שבטי שודדי שמיים, כרו גבישי סערה והגנו מפני מצודות יריבות.",
  game_underdark_title: "מושבת האנדרדארק",
  game_underdark_desc: "הקימו יישוב מתחת לאדמה בין פטריות זוהרות ושוכני מערות עוינים. נהלו אור כמשאב, גדלו פטריות ותיווכו שלום בין סיעות תת-קרקעיות.",
  game_clockwork_title: "עיר השעון",
  game_clockwork_desc: "חקרו קונספירציות במטרופולין ויקטוריאנית שמופעלת על ידי גלגלי שיניים ענקיים. הצטרפו לאגודות סתר, פתרו חידות מכניות וגלו מי שולט במנגנון הגדול.",
  game_arcane_title: "אקדמיית הקסם",
  game_arcane_desc: "הירשמו לאקדמיה לקסם אסור. השתתפו בהרצאות, התדיינו עם חניכים, חקרו כרכים עתיקים והחליטו אם לציית לסגל או לפתוח את הכספת החתומה.",
  game_sand_title: "ממלכות החול",
  game_sand_desc: "שוטטו במדבר עצום מנוקד בציוויליזציות קבורות. חפרו שרידים, סחרו עם שבטים נוודים, שרדו סופות חול ופענחו נבואה שעלולה להקים או להחריב אימפריה.",
  game_frozen_title: "כס הקרח",
  game_frozen_desc: "הובילו שבט דרך חורף אינסופי. צודו חיות מפלצתיות, פשטו על מחנות אויב, פייסו את האלים הישנים ואחדו שבטי הצפון לפני שהקרח בולע הכל.",
  game_verdant_title: "צריח ירוק",
  game_verdant_desc: "שלטו בעיר צמרות שזורה בעץ-עולם. אזנו בין קסם טבע להתקדמות תעשייתית, נהלו מו״מ עם רוחות יער והגנו מפני כורתים.",
  game_deep_title: "נמל עמוק",
  game_deep_desc: "נהלו עיר תת-ימית בקרקעית האוקיינוס. אזנו אספקת חמצן, סחרו עם ספינות משטח, חקרו יצורים תת-ימיים ומנעו פרצות גוף קטסטרופליות.",
  game_neon_title: "בור ניאון",
  game_neon_desc: "עלו בדרגות זירת קרב סייברפאנק מחתרתית. שדרגו את גופכם, נהלו חסויות, תרגלו קרבות והחליטו אם האליפות שווה את האנושיות.",
  game_plague_title: "מחלקת מגפה",
  game_plague_desc: "אתם מרפאים בעיר ימי ביניים הרוסה ממגפה. מיינו חולים, מצאו עשבי מרפא נדירים, נווטו בפוליטיקת הסגר והחליטו מי חי כשהתרופה נגמרת.",
  game_iron_title: "מסילת הברזל",
  game_iron_desc: "פקדו על רכבת משוריינת שחוצה יבשת שבורה. אספו ניצולים, הגנו מפני שודדים, סחרו דלק במזון ובחרו אילו יישובים להציל — אי אפשר לעצור בכל מקום.",
  game_silk_title: "דרך המשי",
  game_silk_desc: "הובילו שיירת סוחרים במדבר קסום. נהלו מו״מ עם ג׳ינים, חמקו משודדים, גלו ערי נווה מדבר וצברו עושר מסחר בסחורות קסומות.",
  game_hw_title: "השממה",
  game_hw_desc: "הציוויליזציה נפלה. נשארו רק האדמה הסדוקה, סופות רעילות והדברים שצדים בלילה. חפשו בהריסות מוקרנות, סחרו עם ניצולים נואשים והחליטו מה אתם מוכנים להפוך כדי לראות עוד זריחה.",
  game_hh_title: "אחוזת הלחישות",
  game_hh_desc: "ירשתם אחוזה ויקטוריאנית. הדלתות ננעלות מעצמן. המראות מראות את השיקוף הלא נכון. משהו חי בקירות ויודע את שמכם. חקרו חדר אחרי חדר וגלו את ההיסטוריה האפלה של המשפחה.",
  game_hu_title: "ירידה לשאול",
  game_hu_desc: "חצו את נהר הסטיקס. מקחו עם כארון. נווטו בעיגולי המתים, ערמו על שומרים עתיקים ושלפו נשמה מתחום המוות עצמו. כל צעד עמוק יותר מקשה את הדרך חזרה.",
  game_hi_title: "האי",
  game_hi_desc: "המטוס שלכם התרסק על אי מסתורי. אנומליות אלקטרומגנטיות מוזרות. מספרים שחוזרים בכל מקום. ניצולים אחרים עם עבר בלתי אפשרי. משהו זז בג׳ונגל — והאי לא רוצה שתעזבו.",
  game_hg_title: "המשולש",
  game_hg_desc: "ספינה נטושה מופיעה במשולש ברמודה. הצוות שלכם עולה עליה ומגלה שזו לולאת זמן — אותה ספינה, אותם מוות, חוזרים לנצח. שברו את המעגל או הפכו חלק ממנו.",
  game_ha_title: "קסנומורף: חלל עמוק",
  game_ha_desc: "ספינת הכרייה שלכם זיהתה אות לא מוכר. עכשיו משהו על הסיפון — מהיר, אינטליגנטי ומתרבה. אטמו מחיצות, קצבו חמצן, עקבו אחריו וקבעו מי מקבל את תרמיל ההצלה האחרון.",
  game_hv_title: "יום הפלישה",
  game_hv_desc: "הם הגיעו בלי התרעה. ספינות ענק מעל כל עיר גדולה. התקשורת מתה. הצבא נעלם. הובילו קבוצת ניצולים קטנה דרך מטרופולין בוערת ומצאו את ההתנגדות — אם היא קיימת.",
  game_has_title: "מחלקה 13",
  game_has_desc: "אתם מתעוררים במקלט נטוש ללא זיכרון. תיקי מטופלים מתארים ניסויים. מערכת הכריזה מגרגרת קול שמכיר אתכם. משהו מאחורי דלת המחלקה הנעולה מגרד. שחזרו מה קרה — לפני שזה קורה שוב.",
};

const ru: ExploreDict = {
  hero_imagine: "которую вы можете представить",

  feat_combat_label: "Боевая система",
  feat_combat_desc: "Пошаговые бои в стиле D&D",
  feat_org_label: "Управление организацией",
  feat_org_desc: "Стройте гильдии, банды и армии",
  feat_territory_label: "Контроль территории",
  feat_territory_desc: "Захватывайте и защищайте локации",
  feat_mp_label: "Мультиплеер",
  feat_mp_desc: "Играйте с друзьями в реальном времени",
  feat_magic_label: "Система магии",
  feat_magic_desc: "Заклинания, мана и кастомная магия",
  feat_politics_label: "Политика и влияние",
  feat_politics_desc: "Фракции, альянсы и интриги",
  feat_journal_label: "Журнал и экспорт книг",
  feat_journal_desc: "Экспортируйте приключения как истории",
  feat_map_label: "Интерактивная карта мира",
  feat_map_desc: "Визуальный редактор карт и навигация",
  feat_byoai_label: "Свой ИИ",
  feat_byoai_desc: "Свой ИИ для полной творческой свободы",
  feat_research_label: "Модуль исследований",
  feat_research_desc: "Google, Wiki и Fandom встроены",

  cat_all: "Все",
  cat_rts: "Стратегия",
  cat_tycoon: "Тайкун и управление",
  cat_rpg: "Текстовая RPG",
  cat_fanfic: "Вдохновлённые миры",
  cat_horror: "Хоррор и выживание",
  cat_other: "Другие жанры",

  tag_territory_control: "Контроль территории",
  tag_organization: "Организация",
  tag_heat_system: "Система розыска",
  tag_spy_cell: "Шпионская ячейка",
  tag_operations: "Операции",
  tag_intel_resources: "Ресурсы разведки",
  tag_military_command: "Военное командование",
  tag_territory: "Территория",
  tag_resources: "Ресурсы",
  tag_criminal_syndicate: "Криминальный синдикат",
  tag_influence: "Влияние",
  tag_feudal: "Феодальный",
  tag_commanders: "Командиры",
  tag_cell_based: "Ячеечная структура",
  tag_morale: "Мораль",
  tag_economy: "Экономика",
  tag_workforce: "Рабочая сила",
  tag_reputation: "Репутация",
  tag_npc_management: "Управление NPC",
  tag_survival: "Выживание",
  tag_personnel: "Персонал",
  tag_quest_system: "Система квестов",
  tag_magic_system: "Система магии",
  tag_quest_journal: "Журнал квестов",
  tag_morality: "Мораль",
  tag_region_movement: "Перемещение по регионам",
  tag_combat: "Бой",
  tag_lore_discovery: "Открытие лора",
  tag_skill_checks: "Проверки навыков",
  tag_character_progression: "Развитие персонажа",
  tag_inner_voice: "Внутренний голос",
  tag_inventory: "Инвентарь",
  tag_dialogue_system: "Система диалогов",

  comp_freeform: "Свободное взаимодействие",
  comp_branching: "Ветвление и выбор игрока",
  comp_memory: "Постоянная память и лор",
  comp_worldstate: "Динамическое состояние мира",
  comp_custom_worlds: "Свои миры и сценарии",
  comp_character: "Система персонажей",
  comp_narrative: "Управление нарративом",
  comp_rpg: "RPG-механики",
  comp_strategy: "Стратегические системы",
  comp_navigation: "Навигация по миру",
  comp_simulation: "Симуляционные системы",
  comp_ai_flex: "Гибкость ИИ",
  comp_multi_agent: "Мульти-агентный ИИ",
  comp_sharing: "Контент и обмен",
  comp_knowledge: "Внешние знания",
  comp_media: "Медиа и аудио",
  comp_multiplayer: "Мультиплеер",
  comp_economy: "Экономика и ресурсы",
  comp_morality: "Мораль и мировоззрение",
  comp_consequence: "Движок последствий",
  comp_survival: "Механики выживания",
  comp_lore: "Открытие лора",
  comp_export: "Журнал и экспорт книг",
  comp_custom_rules: "Кастомные правила",
  comp_document: "Загрузка и анализ документов",
  comp_label_advanced: "Продвинутый",
  comp_label_full_system: "Полная система",
  comp_label_deep: "Глубоко",
  comp_label_advanced_rpg: "Продвинутая RPG",
  comp_label_ai_director: "ИИ-режиссёр",
  comp_label_strong: "Сильный",
  comp_label_map_based: "На основе карты",
  comp_label_full: "Полный",
  comp_label_byo_ai: "Полный (свой ИИ)",
  comp_label_google_wiki: "Google, Wiki, Fandom",
  comp_label_tts: "Полный (TTS + ещё)",

  mech_combat_title: "Боевая система",
  mech_combat_desc: "Пошаговый бой в стиле D&D 5e с инициативой, экономикой действий (действие, бонусное действие, реакция, перемещение), хитами, КБ и бросками урона.",
  mech_skill_title: "Проверки навыков",
  mech_skill_desc: "Проверки характеристик (СИЛ, ЛОВ, ТЕЛ, ИНТ, МДР, ХАР) с бонусами мастерства, преимуществом/помехой и шкалой сложности.",
  mech_inventory_title: "Инвентарь и предметы",
  mech_inventory_desc: "Грузоподъёмность, слоты снаряжения, свойства предметов, расходники и таблицы лута. Экипировка влияет на характеристики.",
  mech_magic_title: "Система магии",
  mech_magic_desc: "Ячейки заклинаний, пулы маны или кастомная магия. Школы магии, списки заклинаний, требования произнесения и ритуалы.",
  mech_quest_title: "Журнал квестов",
  mech_quest_desc: "Отслеживание основных и побочных квестов. Квесты обнаруживаются, принимаются, продвигаются, завершаются или проваливаются динамически.",
  mech_reputation_title: "Репутация и фракции",
  mech_reputation_desc: "Многофракционное отслеживание репутации. Действия влияют на отношение NPC и организаций — от враждебного до союзного.",
  mech_morality_title: "Система морали",
  mech_morality_desc: "Отслеживание мировоззрения по нескольким осям. Выбор влияет на этическую позицию персонажа и реакции NPC.",
  mech_territory_title: "Контроль территории",
  mech_territory_desc: "Захватывайте, защищайте и саботируйте локации. Управляйте обороной, производством ресурсов и угрозами соперников.",
  mech_org_title: "Управление организацией",
  mech_org_desc: "Стройте организации — гильдии, банды, компании, армии. Набирайте членов, назначайте роли, управляйте моралью и политикой.",
  mech_economy_title: "Экономика и торговля",
  mech_economy_desc: "Валюты, магазины, торговля, крафт и динамическое ценообразование. Ведите бизнес и контролируйте рынки.",
  mech_resources_title: "Ресурсы",
  mech_resources_desc: "Отслеживайте ресурсы (еда, боеприпасы, влияние, розыск). Ресурсы тратятся, восполняются и производятся на контролируемых точках.",
  mech_survival_title: "Выживание",
  mech_survival_desc: "Голод, жажда, усталость и воздействие среды. Собирайте, охотьтесь, очищайте воду и ищите укрытие.",
  mech_dialogue_title: "Система диалогов",
  mech_dialogue_desc: "Глубокие разговоры с NPC: убеждение, запугивание, обман и проницательность. NPC помнят ваши слова и поступки.",
  mech_worldmap_title: "Карта мира",
  mech_worldmap_desc: "Интерактивные карты с размещаемыми точками, подкартами и связыванием локаций. Навигация и детализация регионов.",
  mech_region_title: "Перемещение по регионам",
  mech_region_desc: "Путешествия с затратами времени, случайными встречами, быстрым перемещением и модификаторами местности.",
  mech_time_title: "Время и погода",
  mech_time_desc: "Смена дня/ночи, сезоны и погода, влияющие на геймплей, видимость, расписание NPC и выживание.",
  mech_progression_title: "Развитие персонажа",
  mech_progression_desc: "Повышайте уровень, получайте опыт, открывайте способности через классовые или свободные системы.",
  mech_classes_title: "Классы персонажей",
  mech_classes_desc: "Создавайте классы с уникальными способностями, бонусами характеристик и ветками развития.",
  mech_conditions_title: "Состояния и статусы",
  mech_conditions_desc: "14 состояний в стиле D&D (отравлен, оглушён, невидим и др.) плюс 6 уровней истощения. Влияют на броски и перемещение.",
  mech_influence_title: "Влияние и политика",
  mech_influence_desc: "Системы политической власти — накапливайте влияние, заключайте сделки, формируйте альянсы и плетите интриги.",
  mech_consequence_title: "Движок последствий",
  mech_consequence_desc: "Действия имеют волновые последствия. ИИ отслеживает цепочки причин и следствий, всплывающие часы или сессии спустя.",
  mech_tone_title: "Нарративный тон",
  mech_tone_desc: "Управляйте атмосферой — мрачный реализм, героическое фэнтези, чёрная комедия, хоррор или литературная проза.",
  mech_pacing_title: "Ритм событий",
  mech_pacing_desc: "ИИ-режиссёр управляет кривыми напряжения — чередуя бой, исследование, социальные сцены и отдых.",
  mech_lore_title: "Открытие лора",
  mech_lore_desc: "Скрытый лор раскрывается через исследование, диалоги и осмотр предметов. Отслеживание знаний игрока.",
  mech_inner_title: "Внутренний голос",
  mech_inner_desc: "Внутренний монолог персонажа реагирует на события мыслями, интуицией и моральными размышлениями.",
  mech_card_title: "Карточная игра",
  mech_card_desc: "Коллекционные карты — тяните руки, играйте карты для действий, собирайте колоды. Карты могут быть заклинаниями или предметами.",
  mech_timeline_title: "Хронология",
  mech_timeline_desc: "Визуальная хронология ключевых событий, открытий, смертей и вех вашего приключения.",
  mech_fanfic_title: "Онлайн-исследование",
  mech_fanfic_desc: "Импорт справочных материалов из сети. Встроенный Google, Википедия и Fandom позволяют ИИ впитывать лор, факты и правила мира для обогащения истории.",
  mech_multiplayer_title: "Мультиплеер",
  mech_multiplayer_desc: "Играйте с друзьями. Пошаговые ходы, порядок инициативы или свободная форма — с управлением группой и общими квестами.",
  mech_custom_title: "Кастомные правила",
  mech_custom_desc: "Задавайте правила текстом. ИИ применяет их вместе с встроенными механиками для полностью кастомного геймплея.",
  mech_research_title: "Модуль исследований",
  mech_research_desc: "Встроенный Google, Википедия и Fandom. Автоматический импорт лора, изображений и канонических фактов в базу знаний мира.",

  game_cartel_title: "Войны картелей",
  game_cartel_desc: "Постройте наркоимперию от уличного угла до континентальной операции. Управляйте территорией, вербуйте солдат, подкупайте чиновников и воюйте с конкурентами.",
  game_shadow_title: "Теневая ячейка",
  game_shadow_desc: "Управляйте тайной разведкой времён холодной войны. Вербуйте агентов, планируйте операции, управляйте конспиративными квартирами и вычисляйте двойных агентов.",
  game_galactic_title: "Галактическое завоевание",
  game_galactic_desc: "Командуйте звёздным флотом. Колонизируйте планеты, стройте верфи, исследуйте технологии и ведите стратегические войны.",
  game_godfather_title: "Наследник крёстного отца",
  game_godfather_desc: "Вы унаследовали рушащуюся мафиозную семью. Восстановите её через рэкет, коррупцию и устранение конкурентов — держась подальше от ФБР.",
  game_feudal_title: "Феодальный владыка",
  game_feudal_desc: "Объедините враждующие средневековые провинции. Назначайте лордов, собирайте дани, устраивайте браки и ведите армии против захватчиков.",
  game_rebel_title: "Командование повстанцев",
  game_rebel_desc: "Возглавьте подполье против дистопического режима. Управляйте ячейками, планируйте диверсии, вербуйте людей и вещайте пиратское радио.",
  game_dungeon_title: "Подземелье Inc.",
  game_dungeon_desc: "Управляйте подземельем как бизнесом. Нанимайте монстров, проектируйте ловушки, настраивайте таблицы лута и балансируйте довольство искателей приключений с прибылью.",
  game_potion_title: "Империя зелий",
  game_potion_desc: "Начните с аптекарской лавки и вырастите её в фармацевтическую империю. Добывайте редкие ингредиенты, исследуйте рецепты и обыгрывайте гильдии алхимиков.",
  game_tavern_title: "Хозяин таверны",
  game_tavern_desc: "Постройте величайшую таверну королевства. Нанимайте барменов, закупайте экзотический эль, проводите конкурсы бардов и привлекайте легендарных искателей приключений.",
  game_station_title: "Космостанция Альфа",
  game_station_desc: "Управляйте пограничной космостанцией. Балансируйте кислород, торгуйте с инопланетянами, разрешайте стыковочные споры и обороняйтесь от пиратов — с прибылью.",
  game_crime_title: "Симулятор криминального босса",
  game_crime_desc: "Постройте преступную империю с нуля. Отмывайте деньги, управляйте прикрытиями, подкупайте политиков и балансируйте между властью и тюрьмой.",
  game_gladiator_title: "Школа гладиаторов",
  game_gladiator_desc: "Тренируйте гладиаторов в Древнем Риме. Покупайте рабов, тренируйте бойцов, договаривайтесь с сенаторами и поднимайтесь по социальной лестнице через кровь.",
  game_mercenary_title: "Наёмная рота",
  game_mercenary_desc: "Управляйте вольной ротой наёмников. Берите контракты, управляйте жалованием, снаряжайте бойцов и стройте легенду по всему континенту.",
  game_hogwarts_title: "Зачарованная академия",
  game_hogwarts_desc: "Поступите в школу чародейства. Выберите факультет, осваивайте заклинания, раскрывайте тайны и противостойте тёмным силам — с ИИ в роли мудрого директора.",
  game_westeros_title: "Железные королевства",
  game_westeros_desc: "Играйте за малый дворянский дом на раздираемом войной континенте. Плетите интриги через альянсы, предательства, браки и войны. Холодные ветра всегда близко.",
  game_middleearth_title: "Следопыт древних земель",
  game_middleearth_desc: "Патрулируйте дикие земли как одинокий следопыт. Выслеживайте тёмных тварей, защищайте поселения и раскрывайте растущую тень, угрожающую свободным народам.",
  game_starfleet_title: "Звёздная академия",
  game_starfleet_desc: "Поступите в элитную академию космофлота. Изучайте ксенолингвистику, проходите невозможные симуляции и получите назначение на звездолёт.",
  game_jedi_title: "Изгнанник Ордена",
  game_jedi_desc: "Падший рыцарь древнего ордена, преследуемый растущей империей. Прячьтесь, сопротивляйтесь коррупции и решите — сражаться или исчезнуть навсегда.",
  game_peaky_title: "Банда бритв: Восхождение",
  game_peaky_desc: "Постройте преступную империю в промышленном городе после войны. Управляйте ипподромами, воюйте с бандами, подкупайте чиновников и расширяйтесь до парламента.",
  game_naruto_title: "Деревня теней",
  game_naruto_desc: "Возглавьте свою скрытую деревню ниндзя. Тренируйте воинов, назначайте миссии, управляйте альянсами и готовьтесь к следующей великой войне шиноби.",
  game_darkest_title: "Тёмный спуск",
  game_darkest_desc: "Исследуйте проклятое мегаподземелье этаж за этажом. Управляйте светом, рассудком и тающими припасами. Каждая дверь — ставка между сокровищем и жуткой смертью.",
  game_wasteland_title: "Скиталец пустошей",
  game_wasteland_desc: "Выживайте в постапокалиптических пустошах. Собирайте еду, воду и боеприпасы. Торгуйте с поселениями и раскройте причину падения.",
  game_court_title: "Двор шипов",
  game_court_desc: "Смертный в ловушке фейского двора. Каждое слово — контракт, каждый подарок с подвохом, а красота скрывает жестокость.",
  game_pirate_title: "Пиратский капитан",
  game_pirate_desc: "Командуйте пиратским кораблём в Карибском море. Управляйте командой, грабьте торговые суда, ищите клады и станьте самым страшным именем на морях.",
  game_vampire_title: "Вампир: Вечная ночь",
  game_vampire_desc: "Новообращённый вампир в обществе нежити. Питайтесь тайно, стройте домен, лавируйте в политике старейшин и выбирайте между человечностью и силой.",
  game_courtroom_title: "Судебная драма",
  game_courtroom_desc: "Адвокат защиты в суровом городе. Допрашивайте свидетелей, ищите улики, стройте дело и выступайте перед присяжными. У каждого клиента свои тайны.",
  game_island_title: "Остров выживания",
  game_island_desc: "Застряли после авиакатастрофы. Стройте укрытие, ищите еду, исследуйте тайны острова и решайте — сотрудничать с выжившими или конкурировать.",
  game_heist_title: "Команда ограбления",
  game_heist_desc: "Соберите команду специалистов для идеального ограбления. Разведайте цель, наберите таланты, соберите снаряжение и действуйте — или импровизируйте.",
  game_political_title: "Политический триллер",
  game_political_desc: "Баллотируйтесь в президенты вымышленной страны. Ведите кампанию, дебатируйте с соперниками, управляйте скандалами и лавируйте между донорами.",
  game_sky_title: "Небесная цитадель",
  game_sky_desc: "Правьте парящей крепостью над облаками. Управляйте воздушным флотом, торгуйте с небесными пиратами, добывайте штормовые кристаллы и отбивайтесь от конкурентов.",
  game_underdark_title: "Колония Подземья",
  game_underdark_desc: "Основайте поселение глубоко под землёй среди светящихся грибов и враждебных пещерных жителей. Управляйте светом как ресурсом и налаживайте мир.",
  game_clockwork_title: "Город часовых механизмов",
  game_clockwork_desc: "Расследуйте заговоры в викторианском мегаполисе на гигантских шестернях. Вступайте в тайные общества и раскройте, кто контролирует Великий Механизм.",
  game_arcane_title: "Аркановая академия",
  game_arcane_desc: "Поступите в академию запретной магии. Посещайте лекции, дуэлите с однокурсниками и решите — подчиняться факультету или вскрыть запечатанный свод.",
  game_sand_title: "Песчаные королевства",
  game_sand_desc: "Бродите по огромной пустыне с погребёнными цивилизациями. Раскапывайте реликвии, торгуйте с кочевниками, переживайте песчаные бури и разгадайте пророчество.",
  game_frozen_title: "Ледяной трон",
  game_frozen_desc: "Ведите клан через бесконечную зиму. Охотьтесь, совершайте набеги, умиротворяйте древних богов и объединяйте северные племена, пока лёд не поглотил всё.",
  game_verdant_title: "Зелёный шпиль",
  game_verdant_desc: "Правьте городом в кронах мирового дерева. Балансируйте магию природы и прогресс, договаривайтесь с лесными духами и защищайтесь от лесорубов.",
  game_deep_title: "Глубокая гавань",
  game_deep_desc: "Управляйте подводным городом на дне океана. Балансируйте кислород, торгуйте с поверхностью, изучайте глубоководных существ и предотвращайте пробоины.",
  game_neon_title: "Неоновая яма",
  game_neon_desc: "Поднимайтесь в подпольном киберпанк-ринге. Улучшайте тело, управляйте спонсорами, подстраивайте бои и решите — стоит ли чемпионство вашей человечности.",
  game_plague_title: "Чумной барак",
  game_plague_desc: "Вы целитель в средневековом городе, охваченном чумой. Сортируйте пациентов, добывайте редкие травы и решайте, кто выживет, когда лекарства заканчиваются.",
  game_iron_title: "Железная дорога",
  game_iron_desc: "Командуйте бронепоездом через разрушенный континент. Подбирайте выживших, отбивайтесь от рейдеров, меняйте топливо на еду — нельзя остановиться везде.",
  game_silk_title: "Шёлковый путь",
  game_silk_desc: "Ведите караван через волшебную пустыню. Торгуйте с джиннами, избегайте бандитов, открывайте оазисы и копите богатство на зачарованных товарах.",
  game_hw_title: "Пустошь",
  game_hw_desc: "Цивилизация пала. Остались потрескавшаяся земля, токсичные бури и существа, охотящиеся ночью. Собирайте в облучённых руинах, торгуйте с отчаявшимися и решайте, во что вы готовы превратиться.",
  game_hh_title: "Шепчущее поместье",
  game_hh_desc: "Вы унаследовали викторианский особняк. Двери запираются сами. Зеркала показывают не то отражение. Что-то живёт в стенах и знает ваше имя. Исследуйте комнату за комнатой.",
  game_hu_title: "Сошествие в Подземный мир",
  game_hu_desc: "Пересеките Стикс. Торгуйтесь с Хароном. Пройдите круги мёртвых, перехитрите древних стражей и спасите душу из царства смерти. Каждый шаг вглубь усложняет возврат.",
  game_hi_title: "Остров",
  game_hi_desc: "Ваш самолёт разбился на загадочном острове. Электромагнитные аномалии. Повторяющиеся числа. Выжившие с невозможным прошлым. Что-то движется в джунглях — и остров не хочет вас отпускать.",
  game_hg_title: "Треугольник",
  game_hg_desc: "Заброшенный корабль в Бермудском треугольнике. Ваш экипаж поднимается на борт и обнаруживает временную петлю — те же смерти, повторяющиеся вечно. Разорвите цикл или станьте его частью.",
  game_ha_title: "Ксеноморф: Глубокий космос",
  game_ha_desc: "Ваше добывающее судно обнаружило неизвестный сигнал. Теперь на борту что-то — быстрое, умное и размножающееся. Герметизируйте переборки, распределяйте кислород и решите, кто получит последнюю спасательную капсулу.",
  game_hv_title: "День вторжения",
  game_hv_desc: "Они пришли без предупреждения. Огромные корабли над каждым крупным городом. Связь мертва. Военные исчезли. Ведите группу выживших через горящий мегаполис и найдите сопротивление — если оно существует.",
  game_has_title: "Палата 13",
  game_has_desc: "Вы просыпаетесь в заброшенной лечебнице без памяти. В картах пациентов — эксперименты. Динамик хрипит голосом, который вас знает. Что-то за запертой дверью палаты царапается. Восстановите, что произошло — пока не повторилось.",
};

const dicts: Record<string, ExploreDict> = { en, he, ru };

export function getExploreDict(locale: string): Record<string, string> {
  return (dicts[locale] || en) as Record<string, string>;
}

export function useExploreTranslations() {
  // This is a simple hook-compatible function that will be called with the locale from useLocale
  return (locale: string, key: string): string => {
    const dict = dicts[locale] || en;
    return dict[key] || en[key] || key;
  };
}
